
import { mapActions, mapGetters } from 'vuex';
import OrganizationSelect from '@/components/account/OrganizationSelect';
import { Logout } from '@/constants/actions';
import { GetActiveOrganizationRole, GetOrganizations, GetOrganizationId } from '@/constants/getters';
import { ROLE_TYPE } from '@/constants';
import { Routes } from '@/constants/routes';
import { supportURL } from '@/utils/account-utils';

export default {
  name: 'EmptyLayout',

  components: {
    OrganizationSelect,
  },

  layout: 'empty',

  props: {
    error: { type: Object, default: null },
  },

  data() {
    return {};
  },

  head() {
    return {
      title: 'Oops! Something went wrong.',
    };
  },

  computed: {
    ...mapGetters('authStore', {
      getActiveOrganizationRole: GetActiveOrganizationRole,
      organizations: GetOrganizations,
      organizationId: GetOrganizationId,
    }),

    message() {
      const { statusCode } = this.error;

      switch (statusCode) {
        case 403:
          return {
            title: `It looks like you don't have access to this organization.`,
            message: 'Contact the owner of the organization to be added as a member.',
            buttonText: 'Sign out',
            buttonAction: () => this.logout(),
          };

        case 403.1:
          return {
            title: `You have been removed from ${this.error.organizationName}.`,
            message:
              'Select another organization in the dropdown to the left or contact an owner/admin, if this was a mistake.',
            buttonText: 'Sign out',
            buttonAction: () => this.logout(),
          };

        case 404:
          return {
            title: `Oops! It's a 404.`,
            message: `We can't find the specified resource.`,
            buttonText: 'Go to dashboard',
            buttonAction: () => this.$navigator.replace(Routes.Dashboard),
          };

        case 402.1:
          if (this.getActiveOrganizationRole(ROLE_TYPE.OWNER)) {
            return {
              title: 'Your free trial period has expired.',
              message: 'To keep using Amplify please upgrade to one of our plans.',
              buttonText: 'See all plans',
              buttonAction: () => this.$navigator.replace(Routes.Plans),
            };
          }

          return {
            title: 'Current organization has not paid for a plan.',
            message: 'Contact the owner of the organization to manage the subscription.',
            buttonText: 'Sign out',
            buttonAction: () => this.logout(),
          };

        case 402.2:
          if (this.getActiveOrganizationRole(ROLE_TYPE.OWNER)) {
            return {
              title: 'Your plan has been cancelled.',
              message: 'To keep using Amplify, please upgrade to one of our plans.',
              buttonText: 'See all plans',
              buttonAction: () => this.$navigator.replace(Routes.Plans),
            };
          }

          return {
            title: 'Current organization has not paid for a plan.',
            message: 'Contact the owner of the organization to manage the subscription.',
            buttonText: 'Sign out',
            buttonAction: () => this.logout(),
          };

        case 402.3:
          if (this.getActiveOrganizationRole(ROLE_TYPE.OWNER)) {
            return {
              title: 'Payment issue.',
              message: 'Your card does not seem to be working, please add a new card to keep using Amplify.',
              buttonText: 'Manage billing',
              buttonAction: () => this.$navigator.replace(Routes.Billing),
            };
          }

          return {
            title: 'Current organization has not paid for a plan.',
            message: 'Contact the owner of the organization to manage the subscription.',
            buttonText: 'Sign out',
            buttonAction: () => this.logout(),
          };

        case 402:
          if (this.getActiveOrganizationRole(ROLE_TYPE.OWNER)) {
            return {
              title: 'Current organization has not paid for a plan.',
              message: 'To use Amplify please upgrade to one of our plans.',
              buttonText: 'See all plans',
              buttonAction: () => this.$navigator.replace(Routes.Plans),
            };
          }

          return {
            title: 'Current organization has not paid for a plan.',
            message: 'Contact the owner of the organization to manage the subscription.',
            buttonText: 'Sign out',
            buttonAction: () => this.logout(),
          };

        case 410.1:
          return {
            title: 'Your invitation is no longer valid',
            message: `It might have already been used or expired. Contact the admin of the organization to get a new invitation or try to login to the organization.`,
            buttonText: 'Login',
            buttonAction: () => this.$navigator.replace('/login'),
          };

        default:
          return {
            title: 'Oops! Something went wrong.',
            message: 'An error has occurred and we are working to fix the problem.',
            buttonText: 'Go to dashboard',
            buttonAction: () => this.$navigator.replace(Routes.Dashboard),
          };
      }
    },
  },

  methods: {
    ...mapActions('authStore', {
      logout: Logout,
    }),

    onLogoClick() {
      this.$navigator.push(Routes.Dashboard);
    },

    onContactSupportClick() {
      window.open(supportURL, '_blank').focus();
    },
  },
};
